<template>
  <div class="packages-page">
    <header class="page-heading">
      <div>
        <p>Acquisitions</p>
        <h1>Colis</h1>
        <span
          >Les frais d’un envoi saisis une fois, répartis sur ses lots.</span
        >
      </div>
      <router-link to="/packages/new" class="primary-action"
        >＋ Nouveau colis</router-link
      >
    </header>

    <section class="packages-panel">
      <div v-if="loading" class="empty">Chargement…</div>
      <div v-else-if="error" class="empty error">{{ error }}</div>
      <div v-else-if="!packages.length" class="empty">
        <span class="empty__icon">📦</span>
        <h2>Aucun colis pour l’instant</h2>
        <p>
          Quand un intermédiaire (ZenMarket…) t’envoie plusieurs lots ensemble,
          crée le colis : ses frais de port, d’emballage et la TVA sont répartis
          sur chaque lot et reportés dans leurs coûts.
        </p>
        <router-link to="/packages/new" class="primary-action"
          >Créer le premier colis</router-link
        >
      </div>
      <table v-else>
        <thead>
          <tr>
            <th>Colis</th>
            <th>Statut</th>
            <th>Lots</th>
            <th>Port et colis</th>
            <th>TVA et douane</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in packages"
            :key="item._key"
            tabindex="0"
            @click="open(item)"
            @keydown.enter="open(item)"
          >
            <td>
              <strong>{{ item.reference }}</strong>
              <small
                >{{ senderLabel(item) }} · envoyé le
                {{ formatDate(item.shipped_on || item.created_at) }}</small
              >
            </td>
            <td class="shipment">
              <span
                :class="[
                  'state',
                  item.received_on ? 'state--received' : 'state--transit',
                ]"
                >{{
                  item.received_on
                    ? `Reçu le ${formatDate(item.received_on)}`
                    : "En route"
                }}</span
              ><a
                v-if="item.tracking_url"
                :href="item.tracking_url"
                target="_blank"
                rel="noopener noreferrer"
                class="track"
                @click.stop
                ><span class="track__long">Suivre le colis</span
                ><span class="track__short">Suivre</span> ↗</a
              >
            </td>
            <td>{{ item.lot_count }}</td>
            <td>
              {{
                formatMoney(
                  (item.totals?.shipping || 0) + (item.totals?.package || 0),
                )
              }}
            </td>
            <td>
              {{ formatMoney(item.totals?.taxes) }}
              <small>{{
                item.import_mode === "actual" ? "montant payé" : "calculée"
              }}</small>
            </td>
            <td>
              <strong>{{ formatMoney(item.totals?.totalCost) }}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
import { formatDate as formatDateText } from "../../clock";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import config from "@/config/config";
import { PACKAGE_SENDERS } from "../../sources";

export default {
  name: "PackagesList",
  setup() {
    const router = useRouter();
    const packages = ref([]);
    const loading = ref(true);
    const error = ref("");
    const names = { zenmarket: "ZenMarket", buyee: "Buyee" };

    const load = async () => {
      loading.value = true;
      try {
        const { data } = await axios.get(`${config.apiUrl}/packages`);
        packages.value = Array.isArray(data) ? data : [];
      } catch (failure) {
        error.value = "Impossible de charger les colis.";
      } finally {
        loading.value = false;
      }
    };
    const formatMoney = (value) =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value) || 0);
    const formatDate = (value) => formatDateText(value);
    // "ZenMarket", "Particulier — Jean (Leboncoin)"; before the sender
    // existed: the intermediary, else a direct purchase
    const senderLabel = (item) => {
      const sender = item.sender || item.intermediary;
      const label = names[sender] || PACKAGE_SENDERS[sender] || "Achat direct";
      return item.sender_name ? `${label} — ${item.sender_name}` : label;
    };
    const open = (item) => router.push(`/packages/${item._key}`);

    onMounted(load);
    return {
      error,
      formatDate,
      formatMoney,
      senderLabel,
      loading,
      open,
      packages,
    };
  },
};
</script>

<style scoped>
.shipment .state {
  display: inline-block;
  white-space: nowrap;
}
.track__short {
  display: none;
}
.shipment .track {
  display: block;
  margin-top: 4px;
  white-space: nowrap;
}
.state {
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
}
.state--transit {
  background: #fff4e5;
  color: #9a5b00;
}
.state--received {
  background: #e8f5ee;
  color: #146c4d;
}
.track {
  color: #2e6098;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.packages-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
}
.page-heading,
.packages-panel {
  max-width: var(--content-width);
  margin-inline: auto;
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.primary-action {
  display: inline-flex;
  height: 40px;
  align-items: center;
  padding: 0 16px;
  border-radius: 8px;
  background: var(--green);
  color: #fff;
  font-size: 13px;
  font-weight: 750;
  text-decoration: none;
  white-space: nowrap;
}
.packages-panel {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th {
  padding: 11px 16px;
  border-bottom: 1px solid var(--line);
  background: #fff;
  color: #7d8796;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
}
td {
  padding: 13px 16px;
  border-bottom: 1px solid #edf0f4;
  font-size: 13px;
  vertical-align: middle;
}
tbody tr {
  background: #fff;
  cursor: pointer;
}
tbody tr:hover,
tbody tr:focus-visible {
  background: #f7f9fb;
  outline: none;
}
td strong {
  display: block;
}
td small {
  display: block;
  margin-top: 3px;
  color: var(--muted);
  font-size: 11px;
}
.empty {
  display: flex;
  min-height: 300px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  padding: 32px 24px;
  color: var(--muted);
  text-align: center;
}
.empty.error {
  color: #c84642;
}
.empty__icon {
  font-size: 28px;
}
.empty h2 {
  margin: 4px 0 0;
  color: var(--text);
  font-size: 17px;
}
.empty p {
  max-width: 520px;
  margin: 0 0 10px;
  font-size: 13px;
}
@media (max-width: 640px) {
  .packages-page {
    padding: 20px 14px;
  }
  .page-heading {
    align-items: stretch;
    flex-direction: column;
  }
  .primary-action {
    justify-content: center;
  }
  /* Phone: package, status and total (lots and costs in the package) */
  th:nth-child(3),
  td:nth-child(3),
  th:nth-child(4),
  td:nth-child(4),
  th:nth-child(5),
  td:nth-child(5) {
    display: none;
  }
  .track__long {
    display: none;
  }
  .track__short {
    display: inline;
  }
}
</style>
